"use client";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Badge } from "../components/ui/Badge";
import { Switch } from "../components/ui/Switch";
import { useState, useEffect } from "react";
import { brandService, Brand } from "../services/brands";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, TrendingUp, X, ChevronLeft, ChevronRight, ImageIcon, ExternalLink, Loader2 } from "lucide-react";
import { getImageUrl } from "../utils";
import { apiFormData } from "../services/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/AlertDialog";

interface MarcasPageProps {
  onNavigate?: (page: string) => void;
}

export function MarcasPage({ onNavigate: _onNavigate }: MarcasPageProps = {}) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddPopup, setShowAddPopup] = useState<{ visible: boolean; editingId?: number }>({ visible: false });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [popupData, setPopupData] = useState({
    name: "",
    image: "",
    active: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ visible: boolean; id?: number }>({ visible: false });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await brandService.getAll();
      setBrands(response.data);
    } catch (error) {
      toast.error("Erro ao carregar marcas", {
        description: "Não foi possível buscar as marcas.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const filteredBrands = brands.filter(b =>
    b.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const currentBrands = filteredBrands.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const openAddPopup = () => {
    setPopupData({ name: "", image: "", active: true });
    setShowAddPopup({ visible: true });
  };

  const closeAddPopup = () => {
    setShowAddPopup({ visible: false });
  };

  const savePopupData = async () => {
    try {
      if (showAddPopup.editingId) {
        await brandService.update(showAddPopup.editingId, popupData);
        toast.success("Marca atualizada com sucesso");
      } else {
        await brandService.create(popupData);
        toast.success("Marca criada com sucesso");
      }
      fetchBrands();
      closeAddPopup();
    } catch (error: any) {
      toast.error("Erro ao salvar", {
        description: error.response?.data?.message || "Erro ao salvar a marca.",
      });
    }
  };

  const handleEdit = (brand: Brand) => {
    setPopupData({ name: brand.name, image: brand.image || "", active: brand.active });
    setShowAddPopup({ visible: true, editingId: brand.id });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'brands');

      const response = await apiFormData.post('/api/admin/upload/send', formData);
      setPopupData({ ...popupData, image: response.data.path ?? "" });
      toast.success("Imagem enviada com sucesso");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  };

  const getStoreUrl = (slug: string) => {
    const storeUrl = import.meta.env.VITE_STORE_URL || "";
    return `${storeUrl}/${slug}`;
  };

  const handleDelete = async (id: number) => {
    setDeleteConfirm({ visible: true, id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      await brandService.delete(deleteConfirm.id);
      toast.success("Marca excluída com sucesso");
      fetchBrands();
    } catch (error: any) {
      toast.error("Erro ao excluir", {
        description: error.response?.data?.message || "Erro ao excluir.",
      });
    } finally {
      setDeleteConfirm({ visible: false });
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Marcas</h1>
        <p className="text-[#68686A]">Gerencie as marcas dos seus produtos</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar marcas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
          />
        </div>
        <Button
          onClick={openAddPopup}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Marca
        </Button>
      </div>

      {!loading && brands.length > 0 && (
        <div className="mb-8">
          <Card className="border border-gray-200 bg-white/50 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#68686A] flex items-center gap-2 text-sm uppercase tracking-wider font-bold">
                <TrendingUp className="h-4 w-4 text-[#D4C070]" />
                Marcas com Mais Produtos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {[...brands]
                  .sort((a, b) => (b.products_count ?? 0) - (a.products_count ?? 0))
                  .slice(0, 5)
                  .map((brand, index) => (
                    <div key={brand.id} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100 shadow-sm">
                      <span className="flex-shrink-0 w-8 h-8 bg-[#D4C070]/10 text-[#D4C070] text-sm font-bold rounded-full flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate text-sm">{brand.name}</p>
                        <p className="text-xs text-[#849D70] font-medium">{brand.products_count ?? 0} produtos</p>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4C070]"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {currentBrands.map((brand) => (
            <Card key={brand.id} className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0 border border-gray-200">
                      {brand.image ? (
                        <img src={getImageUrl(brand.image)} alt={brand.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2 text-gray-900">
                        {brand.name}
                        <a 
                          href={getStoreUrl(brand.slug)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#D4C070] hover:text-[#849D70] transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {!brand.active && (
                          <Badge variant="secondary" className="text-xs bg-[#C2452D] text-white border-[#C2452D]">
                            Inativa
                          </Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                        <span>{brand.slug}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#68686A]">Produtos:</span>
                    <span className="font-medium text-gray-900">{brand.products_count ?? 0}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(brand)}
                    className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                  >
                    <Edit className="h-3 w-3 mr-2" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(brand.id)}
                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mb-8">
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="border-gray-300 text-[#68686A]"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-1">
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  // Mostrar apenas algumas páginas se houver muitas
                  if (
                    totalPages > 7 &&
                    page !== 1 &&
                    page !== totalPages &&
                    Math.abs(page - currentPage) > 1
                  ) {
                    if (page === 2 || page === totalPages - 1) return <span key={page} className="px-1">...</span>;
                    return null;
                  }
                  
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page 
                        ? "bg-[#D4C070] hover:bg-[#D4C070] text-white border-none" 
                        : "border-gray-300 text-[#68686A]"}
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="icon"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="border-gray-300 text-[#68686A]"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Popup de adição de marca */}
      {showAddPopup.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/20 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative border border-gray-200 shadow-xl">
            <button
              onClick={closeAddPopup}
              className="absolute top-3 right-3 text-white bg-[#D4C070] hover:bg-[#D4C070] rounded-full p-1 cursor-pointer z-[60]"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              {showAddPopup.editingId ? "Editar Marca" : "Adicionar Marca"}
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-2">Logo da Marca</Label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center relative group">
                    {popupData.image ? (
                      <>
                        <img src={getImageUrl(popupData.image)} alt="Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button 
                            onClick={() => setPopupData({ ...popupData, image: "" })}
                            className="text-white hover:text-red-400"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-2">
                        {uploadingImage ? (
                          <Loader2 className="h-6 w-6 animate-spin text-[#D4C070] mx-auto" />
                        ) : (
                          <>
                            <ImageIcon className="h-6 w-6 text-gray-400 mx-auto" />
                            <span className="text-[10px] text-gray-500 mt-1 block">Clique abaixo</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="text-xs border-gray-300 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#D4C070]/10 file:text-[#D4C070] hover:file:bg-[#D4C070]/20"
                    />
                    <p className="text-[10px] text-gray-500">Recomendado: 400x400px. Máx: 2MB.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="item-name" className="block text-sm font-medium text-gray-700">Nome</Label>
                <Input
                  id="item-name"
                  value={popupData.name}
                  onChange={(e) => setPopupData({ ...popupData, name: e.target.value })}
                  placeholder="Nome da marca"
                  className="border-gray-300"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label htmlFor="item-active" className="text-sm font-medium text-gray-700">Status</Label>
                  <p className="text-xs text-gray-500">Define se a marca está ativa para os produtos</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${popupData.active ? 'text-[#849D70]' : 'text-[#C2452D]'}`}>
                    {popupData.active ? 'Ativa' : 'Inativa'}
                  </span>
                  <Switch
                    id="item-active"
                    checked={popupData.active}
                    onCheckedChange={(checked) => setPopupData({ ...popupData, active: checked })}
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeAddPopup}
                className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
              >
                Cancelar
              </Button>
              <Button
                onClick={savePopupData}
                className="bg-[#D4C070] hover:bg-[#849D70] text-white"
              >
                Salvar
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={deleteConfirm.visible} onOpenChange={(open) => !open && setDeleteConfirm({ visible: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Isso excluirá permanentemente a marca e poderá afetar os produtos vinculados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#C2452D] hover:bg-[#A13925] text-white border-none cursor-pointer">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

export default MarcasPage;
